import { useEffect, useState } from "react";
import { domain, token } from "../../components/Security";
import { format } from "date-fns";
import axios from "../.../../../api/apiClient";
import { Select } from "../Select";

export const PayrollPeriodSelect = ({
  // Select component props
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  setValue,
  placeholder = "Select payroll period",
  className = "",
  parentClassName = "",
  error = false,
  errorMessage = "",
  isLoading = false,
  allowSelectAll = false,

  // Legacy props
  data,
  setData,
  isEditable,

  // Additional props
  showPayType = true,
  dateFormat = 'MM/dd/yyyy',
  includeInactive = false,
  hasPayTypeId = false,
  payTypeId,

  ...props
}) => {
  const [payrollPeriods, setPayrollPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);

  // Legacy mode detection
  const isLegacyMode = data !== undefined || setData !== undefined;
  
  // Determine value for Select component
  const selectValue = isLegacyMode ? data?.id?.toString() || "" : value || "";
  
  // Determine disabled state
  const disabled = isEditMode || isDisabled || loading || isLoading || 
                  (isLegacyMode && !isEditable) || 
                  (hasPayTypeId && !payTypeId); // Disable if hasPayTypeId is true but no payTypeId

  useEffect(() => {
    const fetchData = async () => {
      // If hasPayTypeId is true but no payTypeId is provided, don't fetch
      if (hasPayTypeId && !payTypeId) {
        setPayrollPeriods([]);
        setOptions([]);
        return;
      }

      try {
        setLoading(true);
        
        let endpoint;
        
        if (!hasPayTypeId) {
          // Use active endpoint without paytype filter
          endpoint = includeInactive 
            ? `/api/PayrollPeriod` 
            : `/api/PayrollPeriod/active`;
        } else {
          // Use endpoint with paytype filter
          endpoint = includeInactive 
            ? `/api/PayrollPeriod` 
            : `/api/PayrollPeriod/active/paytype/${payTypeId}`;
        }
        
        const response = await axios.get(domain + endpoint, {
          headers: { 
            Authorization: `Bearer ${token}`, 
            "Content-Type": "application/json" 
          },
        });
        
        // Sort by date descending
        const sortedData = response.data.sort((a, b) =>
          (b.dateFrom || "").localeCompare(a.dateFrom || "")
        );
        
        setPayrollPeriods(sortedData);
        
        // Transform to options format for Select component
        const transformedOptions = sortedData.map(period => {
          const formattedFrom = format(new Date(period.dateFrom), dateFormat);
          const formattedTo = format(new Date(period.dateTo), dateFormat);
          let label = `${formattedFrom} - ${formattedTo}`;
          
          if (showPayType && period.payTypeDescription) {
            label += ` (${period.payTypeDescription})`;
          }
          
          // Add inactive indicator
          if (includeInactive && !period.isActive) {
            label += " [Inactive]";
          }
          
          return {
            value: period.id.toString(),
            label: label,
            rawData: period
          };
        });
        
        setOptions(transformedOptions);
      } catch (err) {
        console.error("Error fetching payroll periods:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [includeInactive, showPayType, dateFormat, payTypeId, hasPayTypeId]);

  const handleChange = (e) => {
    const selectedValue = e.target.value;
    
    if (isLegacyMode) {
      // Legacy mode: find the full object and pass it to setData
      const found = payrollPeriods.find(p => 
        p.id?.toString() === selectedValue?.toString()
      );
      if (setData) {
        setData(selectedValue === "" || selectedValue === "0" ? null : found);
      }
    } else {
      // New mode: handle both onChange and setValue patterns
      if (onChange) {
        onChange(e);
      } else if (setValue) {
        setValue(selectedValue);
      }
    }
  };

  // Show placeholder when hasPayTypeId is true but no payTypeId
  if (hasPayTypeId && !payTypeId) {
    return (
      <Select
        label={label}
        isRequired={isRequired}
        isDisabled={true}
        isEditMode={isEditMode}
        value=""
        onChange={handleChange}
        options={[]}
        placeholder="Select pay type first"
        className={className}
        parentClassName={parentClassName}
        error={error}
        errorMessage={errorMessage}
        {...props}
      />
    );
  }

  // Handle loading state
  if (loading) {
    return (
      <Select
        label={label}
        isRequired={isRequired}
        isDisabled={true}
        isEditMode={isEditMode}
        value={selectValue}
        onChange={handleChange}
        options={[]}
        placeholder="Loading payroll periods..."
        className={className}
        parentClassName={parentClassName}
        error={error}
        errorMessage={errorMessage}
        allowSelectAll={allowSelectAll}
        {...props}
      />
    );
  }

  return (
    <Select
      label={label}
      isRequired={isRequired}
      isDisabled={disabled}
      isEditMode={isEditMode}
      value={selectValue}
      onChange={handleChange}
      options={options}
      placeholder={placeholder}
      className={className}
      parentClassName={parentClassName}
      error={error}
      errorMessage={errorMessage}
      allowSelectAll={allowSelectAll}
      {...props}
    />
  );
};